import type { ReactNode } from "react";
import { cn } from "../utils/cn";
import { Badge } from "../primitives/Badge";
import { Button } from "../primitives/Button";
import { Dropdown } from "../primitives/Dropdown";

type MetaItem = {
  label: string;
  value: string;
  tone?: "default" | "muted" | "accent" | "danger";
};

type Status = {
  label: string;
  tone: "success" | "warning" | "error" | "info" | "neutral";
};

type Action = {
  label: string;
  onClick?: () => void;
  icon?: ReactNode;
  disabled?: boolean;
};

export type EntityHeaderPatternProps = {
  title: string;
  subtitle?: string;
  metaItems?: MetaItem[];
  status?: Status;
  primaryAction?: Action;
  secondaryActions?: Action[];
  rightSlot?: ReactNode;
  isLoading?: boolean;
  compact?: boolean;
};

const metaToneClasses: Record<NonNullable<MetaItem["tone"]>, string> = {
  default: "text-text-secondary",
  muted: "text-muted",
  accent: "text-primary",
  danger: "text-danger",
};

const statusToneMap: Record<Status["tone"], "neutral" | "success" | "warning" | "danger"> = {
  neutral: "neutral",
  success: "success",
  warning: "warning",
  error: "danger",
  info: "neutral",
};

export function EntityHeaderPattern({
  title,
  subtitle,
  metaItems,
  status,
  primaryAction,
  secondaryActions,
  rightSlot,
  isLoading = false,
  compact = false,
}: EntityHeaderPatternProps) {
  const items = metaItems ?? (subtitle ? [{ label: "Info", value: subtitle, tone: "muted" }] : []);

  return (
    <div className="rounded-md border border-border bg-surface p-6 shadow-sm">
      <div className={cn("flex items-center justify-between gap-4", compact && "flex-col items-start")}>
        <div className="space-y-2">
          {isLoading ? (
            <div className="space-y-2">
              <div className="h-4 w-32 rounded bg-surface-muted" />
              <div className="h-6 w-64 rounded bg-surface-muted" />
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <h2 className="text-title text-text font-semibold">{title}</h2>
                {status ? <Badge label={status.label} tone={statusToneMap[status.tone]} /> : null}
              </div>
              <div className="h-0.5 w-12 rounded-full bg-primary/70" />
              {items.length ? (
                <div className="flex flex-wrap gap-3 text-sm text-muted">
                  {items.map((item) => (
                    <span key={`${item.label}-${item.value}`} className={metaToneClasses[item.tone ?? "muted"]}>
                      {item.label}: {item.value}
                    </span>
                  ))}
                </div>
              ) : null}
            </>
          )}
        </div>
        <div className={cn("flex flex-wrap items-center gap-2", compact && "w-full justify-start")}>
          {primaryAction ? (
            <Button
              label={primaryAction.label}
              intent="primary"
              size="sm"
              state={primaryAction.disabled ? "disabled" : "default"}
              onClick={primaryAction.onClick}
            />
          ) : null}
          {secondaryActions && secondaryActions.length ? (
            <Dropdown
              label="Actions"
              items={secondaryActions.map((action) => ({
                label: action.label,
                disabled: action.disabled,
                onClick: action.onClick,
              }))}
              align="right"
            />
          ) : null}
          {rightSlot}
        </div>
      </div>
    </div>
  );
}
